import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../i18n';
import {
  Users, Search, RefreshCw, Filter, MoreVertical, UserCheck, UserX,
  Mail, Calendar, Shield, Building2, Clock, ChevronDown, ChevronUp,
  Edit2, Trash2, Key, Eye, AlertTriangle, CheckCircle, XCircle,
  Crown, User as UserIcon, Activity, Plus, X, Save, History,
  FileText, Target, ArrowLeft, Lock, Unlock, CreditCard, Coins, Sparkles,
  Globe, Zap, Rocket, Settings2, Check, Download, KeyRound, ShieldOff, MailCheck
} from 'lucide-react';
import { toast } from '../utils/toastUtils';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Handle 401 responses by redirecting to login (matches apiClient behavior)
const handle401 = (response) => {
  if (response.status === 401) {
    localStorage.removeItem('access_token');
    const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/magic-login', '/share', '/sign', '/verify-email', '/setup', '/accept-invitation', '/invitation', '/auth-redirect', '/dedicated-server', '/security/verify-login', '/pricing'];
    const currentPath = window.location.pathname;
    if (!publicPaths.some(path => currentPath.startsWith(path))) {
      window.location.href = '/login';
      throw new Error('Session expired - redirecting to login');
    }
  }
  return response;
};

const ClientsManagement = () => {
  const { t } = useI18n();
  const translateScanType = (scanType) => {
    if (!scanType) return '-';
    const key = `scans.types.${scanType}`;
    const translated = t(key);
    if (translated !== key) return translated;
    const osintKey = `osint.scanTypes.${scanType}`;
    const osintTranslated = t(osintKey);
    if (osintTranslated !== osintKey) return osintTranslated;
    return scanType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [companies, setCompanies] = useState([]);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [pagination, setPagination] = useState({ limit: 50, offset: 0 });
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showDetailView, setShowDetailView] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(null);
  const [showCreditsModal, setShowCreditsModal] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Plan & Credits data
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [creditsAmount, setCreditsAmount] = useState(100);
  const [creditsReason, setCreditsReason] = useState('');
  const [userSubscription, setUserSubscription] = useState(null);
  const [userCredits, setUserCredits] = useState(null);

  // User detail data
  const [userScans, setUserScans] = useState([]);
  const [userActions, setUserActions] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'analyst',
    is_active: true
  });

  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  // Fetch companies for filter on mount
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const token = getToken();
        const response = await fetch(`${API_BASE}/api/v1/admin/companies?limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(handle401);
        if (response.ok) {
          const data = await response.json();
          // Sort companies by name, filter out empty names
          const sorted = (data || [])
            .filter(c => c.name && c.name.trim())
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setCompanies(sorted);
        }
      } catch (err) {
        console.error('Error fetching companies:', err);
      }
    };
    loadCompanies();
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();

      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: pagination.offset.toString()
      });

      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter) params.append('role', roleFilter);
      if (companyFilter) params.append('company_id', companyFilter);

      const response = await fetch(`${API_BASE}/api/v1/admin/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);

      if (!response.ok) throw new Error('Failed to fetch users');

      const data = await response.json();
      setUsers(data);

      // Calculate stats from fetched data
      const active = data.filter(u => u.is_active).length;
      setStats({
        total: data.length,
        active,
        inactive: data.length - active
      });
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [pagination, searchTerm, roleFilter, companyFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Create user
  const createUser = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name
        })
      }).then(handle401);

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to create user');
      }

      const newUser = await response.json();

      // Update role if not default
      if (formData.role !== 'viewer') {
        await updateUserRole(newUser.id, formData.role);
      }

      toast.success('User created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      toast.error(err.message || 'Error creating user');
    }
  };

  // Update user status
  const updateUserStatus = async (userId, isActive) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/admin/users/${userId}?is_active=${isActive}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);

      if (!response.ok) throw new Error('Failed to update user');

      toast.success(isActive ? 'User activated' : 'User deactivated');
      fetchUsers();
      setShowActionMenu(null);
    } catch (err) {
      toast.error('Error updating');
    }
  };

  // Update user role
  const updateUserRole = async (userId, role) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/admin/users/${userId}?role=${role}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);

      if (!response.ok) throw new Error('Failed to update role');

      toast.success('Role updated');
      fetchUsers();
      setShowActionMenu(null);
    } catch (err) {
      toast.error('Error updating role');
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);

      if (!response.ok) throw new Error('Failed to delete user');

      toast.success('User deleted');
      setShowDeleteConfirm(null);
      fetchUsers();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Resend invitation email
  const resendInvitation = async (userId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/account/team/${userId}/resend-invitation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur lors du renvoi');
      }

      toast.success('Invitation email resent successfully');
      setShowActionMenu(null);
    } catch (err) {
      toast.error(err.message || 'Erreur lors du renvoi de l\'invitation');
    }
  };

  // Resend verification email (new admin endpoint)
  const resendVerificationEmail = async (userId) => {
    try {
      setActionLoading(true);
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/admin/users/${userId}/resend-verification`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur lors du renvoi');
      }

      toast.success('Verification email resent successfully');
      setShowActionMenu(null);
    } catch (err) {
      toast.error(err.message || 'Erreur lors du renvoi');
    } finally {
      setActionLoading(false);
    }
  };

  // Send password reset email
  const sendPasswordResetEmail = async (userId) => {
    try {
      setActionLoading(true);
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/admin/users/${userId}/send-password-reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur lors de l\'envoi');
      }

      toast.success('Reset email sent');
      setShowActionMenu(null);
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'envoi');
    } finally {
      setActionLoading(false);
    }
  };

  // Direct password reset (admin sets new password)
  const resetUserPassword = async (userId) => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must contain at least 8 characters');
      return;
    }
    try {
      setActionLoading(true);
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ new_password: newPassword })
      }).then(handle401);

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error during reset');
      }

      toast.success('Password changed successfully');
      setShowPasswordModal(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.message || 'Error during reset');
    } finally {
      setActionLoading(false);
    }
  };

  // Unlock account (reset failed attempts)
  const unlockAccount = async (userId) => {
    try {
      setActionLoading(true);
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/admin/users/${userId}/unlock`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error unblocking account');
      }

      toast.success('Account unblocked successfully');
      setShowActionMenu(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.message || 'Error unblocking account');
    } finally {
      setActionLoading(false);
    }
  };

  // Force verify email
  const forceVerifyEmail = async (userId) => {
    try {
      setActionLoading(true);
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/admin/users/${userId}/verify-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error during verification');
      }

      toast.success('Email marked as verified');
      setShowActionMenu(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.message || 'Error during verification');
    } finally {
      setActionLoading(false);
    }
  };

  // Disable MFA
  const disableUserMfa = async (userId) => {
    try {
      setActionLoading(true);
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/admin/users/${userId}/disable-mfa`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error disabling MFA');
      }

      toast.success('MFA disabled successfully');
      setShowActionMenu(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.message || 'Error disabling');
    } finally {
      setActionLoading(false);
    }
  };

  // Export users to CSV
  const exportUsersCSV = async () => {
    try {
      setActionLoading(true);
      const token = getToken();

      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter) params.append('role', roleFilter);
      if (companyFilter) params.append('company_id', companyFilter);

      const response = await fetch(`${API_BASE}/api/v1/admin/users/export?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);

      if (!response.ok) {
        throw new Error('Erreur lors de l\'export');
      }

      // Download as file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('CSV export downloaded');
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'export');
    } finally {
      setActionLoading(false);
    }
  };

  // Fetch user subscription and credits
  const fetchUserSubscription = async (companyId) => {
    if (!companyId) return;
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/admin/companies/${companyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);
      if (response.ok) {
        const data = await response.json();
        setUserSubscription(data.subscription);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };

  const fetchUserCredits = async (companyId) => {
    if (!companyId) return;
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/v1/credits/balance?company_id=${companyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);
      if (response.ok) {
        const data = await response.json();
        setUserCredits(data);
      }
    } catch (err) {
      console.error('Error fetching credits:', err);
    }
  };

  // Update subscription plan
  const updateSubscriptionPlan = async (companyId, plan) => {
    try {
      const token = getToken();

      const response = await fetch(`${API_BASE}/api/v1/admin/companies/${companyId}/subscription`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan })
      }).then(handle401);

      if (!response.ok) throw new Error('Failed to update plan');

      toast.success('Plan updated successfully');
      setShowPlanModal(null);
      setSelectedPlan('free');
      fetchUsers();
    } catch (err) {
      toast.error('Error updating plan');
    }
  };

  // Add bonus credits
  const addBonusCredits = async (companyId) => {
    if (!creditsReason || creditsReason.length < 5) {
      toast.error('Please provide a reason (min 5 characters)');
      return;
    }
    try {
      const token = getToken();
      const params = new URLSearchParams({
        credits_amount: creditsAmount.toString(),
        reason: creditsReason,
        expires_months: '12'
      });
      const response = await fetch(`${API_BASE}/api/v1/credits/admin/bonus?company_id=${companyId}&${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to add credits');
      }

      toast.success(`${creditsAmount} credits added successfully`);
      setShowCreditsModal(null);
      setCreditsAmount(100);
      setCreditsReason('');
    } catch (err) {
      toast.error(err.message || 'Error adding credits');
    }
  };

  // Fetch user details (scans and actions)
  const fetchUserDetails = async (userId, userLastLogin) => {
    setLoadingDetails(true);
    try {
      const token = getToken();

      // If user never connected (last_login_at is null), don't fetch scans
      // They couldn't have created any scans if they never logged in
      if (!userLastLogin) {
        setUserScans([]);
        setUserActions([]);
        setLoadingDetails(false);
        return;
      }

      // Fetch user's scans - only for users who have logged in
      const scansResponse = await fetch(`${API_BASE}/api/v1/scan?created_by=${userId}&limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);
      if (scansResponse.ok) {
        const scansData = await scansResponse.json();
        setUserScans(scansData.scans || []);
      } else {
        setUserScans([]);
      }

      // Fetch user's actions (tracking logs) - use the correct endpoint
      const actionsResponse = await fetch(`${API_BASE}/api/v1/history/user/${userId}?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(handle401);
      if (actionsResponse.ok) {
        const actionsData = await actionsResponse.json();
        setUserActions(actionsData.actions || []);
      } else {
        setUserActions([]);
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
      setUserScans([]);
      setUserActions([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Open user detail view
  const openUserDetail = async (user) => {
    setShowDetailView(user);
    await fetchUserDetails(user.id, user.last_login_at);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      full_name: '',
      role: 'analyst',
      is_active: true
    });
  };

  // Sort users
  const sortedUsers = [...users].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (sortField === 'created_at' || sortField === 'last_login_at') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }

    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  // Filter by status
  const filteredUsers = sortedUsers.filter(user => {
    if (statusFilter === 'active' && !user.is_active) return false;
    if (statusFilter === 'inactive' && user.is_active) return false;
    return true;
  });

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <Crown className="w-4 h-4 text-yellow-400" />;
      case 'admin': return <Shield className="w-4 h-4 text-purple-400" />;
      case 'manager': return <UserCheck className="w-4 h-4 text-blue-400" />;
      case 'analyst': return <Activity className="w-4 h-4 text-green-400" />;
      default: return <UserIcon className="w-4 h-4 text-gray-400" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'admin': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'manager': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'analyst': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Jamais';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // User Detail View
  if (showDetailView) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              setShowDetailView(null);
              setUserScans([]);
              setUserActions([]);
            }}
            className="p-2 hover:bg-brand-black-light rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400" />
          </button>
          <div className="flex items-center gap-4 flex-1">
            <div className="w-16 h-16 rounded-full bg-brand-green/20 flex items-center justify-center">
              <span className="text-brand-green font-bold text-2xl">
                {(showDetailView.username || showDetailView.email || '?')[0].toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{showDetailView.username}</h2>
              <p className="text-gray-400">{showDetailView.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(showDetailView.role)}`}>
                  {getRoleIcon(showDetailView.role)}
                  {showDetailView.role}
                </span>
                {showDetailView.is_active ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
                    <CheckCircle className="w-3 h-3" /> Actif
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
                    <XCircle className="w-3 h-3" /> Inactif
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {showDetailView.is_active ? (
              <button
                onClick={() => updateUserStatus(showDetailView.id, false)}
                className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 flex items-center gap-2"
              >
                <Lock className="w-4 h-4" />
                Bloquer
              </button>
            ) : (
              <button
                onClick={() => updateUserStatus(showDetailView.id, true)}
                className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 flex items-center gap-2"
              >
                <Unlock className="w-4 h-4" />
                Unblock
              </button>
            )}
          </div>
        </div>

        {/* User Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/20">
            <p className="text-gray-500 text-sm mb-1">Nom complet</p>
            <p className="text-white font-medium">{showDetailView.full_name || '-'}</p>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/20">
            <p className="text-gray-500 text-sm mb-1">Last login</p>
            <p className="text-white font-medium">{formatDate(showDetailView.last_login_at)}</p>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/20">
            <p className="text-gray-500 text-sm mb-1">Date d'inscription</p>
            <p className="text-white font-medium">{formatDate(showDetailView.created_at)}</p>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/20">
            <p className="text-gray-500 text-sm mb-1">MFA</p>
            <p className={showDetailView.mfa_enabled ? 'text-green-400' : 'text-gray-400'}>
              {showDetailView.mfa_enabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>
        </div>

        {loadingDetails ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-brand-green" />
          </div>
        ) : (
          <>
            {/* User's Scans */}
            <div className="bg-brand-black-light rounded-xl border border-brand-green/20">
              <div className="p-4 border-b border-gray-800">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-brand-green" />
                  Scans de l'utilisateur ({userScans.length})
                </h3>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {userScans.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    {!showDetailView.last_login_at ? (
                      <div className="flex flex-col items-center gap-2">
                        <AlertTriangle className="w-8 h-8 text-yellow-500/50" />
                        <p>User never logged in</p>
                        <p className="text-xs text-gray-600">No scan can be associated with this user</p>
                      </div>
                    ) : (
                      'No scans found'
                    )}
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-brand-black sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm text-gray-400">Cible</th>
                        <th className="px-4 py-2 text-left text-sm text-gray-400">Type</th>
                        <th className="px-4 py-2 text-left text-sm text-gray-400">Statut</th>
                        <th className="px-4 py-2 text-left text-sm text-gray-400">Date</th>
                        <th className="px-4 py-2 text-left text-sm text-gray-400">Vulns</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {userScans.map((scan) => (
                        <tr key={scan.id} className="hover:bg-brand-black/50">
                          <td className="px-4 py-2 text-white">{scan.target || scan.name || '-'}</td>
                          <td className="px-4 py-2 text-gray-400">{translateScanType(scan.scan_type)}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              scan.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                              scan.status === 'running' ? 'bg-blue-500/20 text-blue-400' :
                              scan.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {scan.status}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-gray-400">{formatDate(scan.created_at)}</td>
                          <td className="px-4 py-2">
                            {scan.total_vulnerabilities > 0 ? (
                              <span className="text-red-400">{scan.total_vulnerabilities}</span>
                            ) : (
                              <span className="text-gray-500">0</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* User's Activity/Actions */}
            <div className="bg-brand-black-light rounded-xl border border-brand-green/20">
              <div className="p-4 border-b border-gray-800">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-brand-green" />
                  Historique des actions ({userActions.length})
                </h3>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                {userActions.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No actions recorded
                  </div>
                ) : (
                  <div className="divide-y divide-gray-800">
                    {userActions.map((action, idx) => (
                      <div key={idx} className="p-4 hover:bg-brand-black/50">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-white font-medium">{action.action_type}</p>
                            <p className="text-gray-400 text-sm">{action.page || action.page_title}</p>
                            {action.metadata && (
                              <p className="text-gray-500 text-xs mt-1">
                                {typeof action.metadata === 'object' ? JSON.stringify(action.metadata) : action.metadata}
                              </p>
                            )}
                          </div>
                          <span className="text-gray-500 text-sm">{formatDate(action.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/20">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-brand-green/20 rounded-lg">
              <Users className="w-6 h-6 text-brand-green" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total Utilisateurs</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-brand-black-light rounded-xl p-4 border border-green-500/20">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <UserCheck className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Actifs</p>
              <p className="text-2xl font-bold text-green-400">{stats.active}</p>
            </div>
          </div>
        </div>

        <div className="bg-brand-black-light rounded-xl p-4 border border-red-500/20">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-500/20 rounded-lg">
              <UserX className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Inactifs</p>
              <p className="text-2xl font-bold text-red-400">{stats.inactive}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/20">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par email, nom, username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-3 flex-wrap">
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
            >
              <option value="">Toutes les entreprises</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
            >
              <option value="">All roles</option>
              <option value="super_admin">Super administrateur</option>
              <option value="admin">Administrateur</option>
              <option value="manager">Gestionnaire</option>
              <option value="analyst">Analyste</option>
              <option value="viewer">Lecteur</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>

            <button
              onClick={fetchUsers}
              disabled={loading}
              className="px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>

            <button
              onClick={exportUsersCSV}
              disabled={actionLoading || loading}
              className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 flex items-center gap-2 disabled:opacity-50"
            >
              <Download className={`w-4 h-4 ${actionLoading ? 'animate-pulse' : ''}`} />
              Exporter CSV
            </button>

            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-brand-green text-black rounded-lg hover:bg-brand-green/90 flex items-center gap-2 font-medium"
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-brand-black-light rounded-xl border border-brand-green/20 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-brand-black">
              <tr>
                <th
                  className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-brand-green"
                  onClick={() => handleSort('username')}
                >
                  <div className="flex items-center gap-2">
                    Utilisateur
                    {sortField === 'username' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-brand-green"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center gap-2">
                    Email
                    {sortField === 'email' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Role</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Statut</th>
                <th
                  className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-brand-green"
                  onClick={() => handleSort('last_login_at')}
                >
                  <div className="flex items-center gap-2">
                    Last login
                    {sortField === 'last_login_at' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-brand-green"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-2">
                    Inscription
                    {sortField === 'created_at' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
                    {t('common.loading')}
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-brand-black/50 transition-colors cursor-pointer"
                    onClick={() => openUserDetail(user)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt="" className="w-10 h-10 rounded-full" />
                          ) : (
                            <span className="text-brand-green font-bold">
                              {(user.username || user.email || '?')[0].toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.username}</p>
                          <p className="text-gray-400 text-sm">{user.full_name || user.display_name || '-'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-300">{user.email}</span>
                        {user.email_verified && (
                          <CheckCircle className="w-4 h-4 text-green-400" title="Email verified" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                        {getRoleIcon(user.role)}
                        {user.role === 'super_admin' ? 'Super administrateur' :
                         user.role === 'admin' ? 'Administrateur' :
                         user.role === 'manager' ? 'Gestionnaire' :
                         user.role === 'analyst' ? 'Analyste' : 'Lecteur'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {user.is_active ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                          <CheckCircle className="w-3.5 h-3.5" />
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
                          <XCircle className="w-3.5 h-3.5" />
                          Inactif
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <Clock className="w-4 h-4" />
                        {formatDate(user.last_login_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <Calendar className="w-4 h-4" />
                        {formatDate(user.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="relative">
                        <button
                          onClick={() => setShowActionMenu(showActionMenu === user.id ? null : user.id)}
                          className="p-2 hover:bg-brand-black rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-5 h-5 text-gray-400" />
                        </button>

                        {showActionMenu === user.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-brand-black-light border border-gray-700 rounded-lg shadow-xl z-50">
                            <div className="py-1">
                              <button
                                onClick={() => openUserDetail(user)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-brand-green/10 hover:text-brand-green flex items-center gap-2"
                              >
                                <Eye className="w-4 h-4" />
                                View details
                              </button>

                              {/* Resend invitation - for users who haven't verified email OR never logged in */}
                              {(!user.email_verified || !user.last_login_at) && (
                                <button
                                  onClick={() => resendInvitation(user.id)}
                                  className="w-full px-4 py-2 text-left text-sm text-blue-400 hover:bg-blue-500/10 flex items-center gap-2"
                                >
                                  <Mail className="w-4 h-4" />
                                  Renvoyer l'invitation
                                </button>
                              )}

                              {/* Resend verification email */}
                              {!user.email_verified && (
                                <button
                                  onClick={() => resendVerificationEmail(user.id)}
                                  disabled={actionLoading}
                                  className="w-full px-4 py-2 text-left text-sm text-cyan-400 hover:bg-cyan-500/10 flex items-center gap-2 disabled:opacity-50"
                                >
                                  <MailCheck className="w-4 h-4" />
                                  Resend email verification
                                </button>
                              )}

                              {/* Force verify email */}
                              {!user.email_verified && (
                                <button
                                  onClick={() => forceVerifyEmail(user.id)}
                                  disabled={actionLoading}
                                  className="w-full px-4 py-2 text-left text-sm text-green-400 hover:bg-green-500/10 flex items-center gap-2 disabled:opacity-50"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Force email verification
                                </button>
                              )}

                              {/* Send password reset email */}
                              <button
                                onClick={() => sendPasswordResetEmail(user.id)}
                                disabled={actionLoading}
                                className="w-full px-4 py-2 text-left text-sm text-purple-400 hover:bg-purple-500/10 flex items-center gap-2 disabled:opacity-50"
                              >
                                <Mail className="w-4 h-4" />
                                Send password reset
                              </button>

                              {/* Direct password change */}
                              <button
                                onClick={() => {
                                  setShowPasswordModal(user);
                                  setShowActionMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-yellow-400 hover:bg-yellow-500/10 flex items-center gap-2"
                              >
                                <KeyRound className="w-4 h-4" />
                                Changer mot de passe
                              </button>

                              {/* Disable MFA */}
                              {user.mfa_enabled && (
                                <button
                                  onClick={() => disableUserMfa(user.id)}
                                  disabled={actionLoading}
                                  className="w-full px-4 py-2 text-left text-sm text-orange-400 hover:bg-orange-500/10 flex items-center gap-2 disabled:opacity-50"
                                >
                                  <ShieldOff className="w-4 h-4" />
                                  Disable MFA
                                </button>
                              )}

                              {/* Unlock account */}
                              <button
                                onClick={() => unlockAccount(user.id)}
                                disabled={actionLoading}
                                className="w-full px-4 py-2 text-left text-sm text-teal-400 hover:bg-teal-500/10 flex items-center gap-2 disabled:opacity-50"
                              >
                                <Key className="w-4 h-4" />
                                Unblock account
                              </button>

                              {user.is_active ? (
                                <button
                                  onClick={() => updateUserStatus(user.id, false)}
                                  className="w-full px-4 py-2 text-left text-sm text-orange-400 hover:bg-orange-500/10 flex items-center gap-2"
                                >
                                  <Lock className="w-4 h-4" />
                                  Bloquer
                                </button>
                              ) : (
                                <button
                                  onClick={() => updateUserStatus(user.id, true)}
                                  className="w-full px-4 py-2 text-left text-sm text-green-400 hover:bg-green-500/10 flex items-center gap-2"
                                >
                                  <Unlock className="w-4 h-4" />
                                  Unblock
                                </button>
                              )}

                              <div className="border-t border-gray-700 my-1"></div>

                              <div className="px-4 py-2 text-xs text-gray-500 uppercase">Change role</div>

                              {['admin', 'manager', 'analyst', 'viewer'].map((role) => (
                                <button
                                  key={role}
                                  onClick={() => updateUserRole(user.id, role)}
                                  disabled={user.role === role}
                                  className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                                    user.role === role
                                      ? 'text-gray-600 cursor-not-allowed'
                                      : 'text-gray-300 hover:bg-brand-green/10 hover:text-brand-green'
                                  }`}
                                >
                                  {getRoleIcon(role)}
                                  {role === 'admin' ? 'Administrateur' :
                                   role === 'manager' ? 'Gestionnaire' :
                                   role === 'analyst' ? 'Analyste' : 'Lecteur'}
                                </button>
                              ))}

                              {user.company_id && (
                                <>
                                  <div className="border-t border-gray-700 my-1"></div>
                                  <div className="px-4 py-2 text-xs text-gray-500 uppercase">Gestion compte</div>

                                  <button
                                    onClick={() => {
                                      setShowPlanModal(user);
                                      setShowActionMenu(null);
                                      fetchUserSubscription(user.company_id);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-blue-400 hover:bg-blue-500/10 flex items-center gap-2"
                                  >
                                    <CreditCard className="w-4 h-4" />
                                    Changer le plan
                                  </button>

                                  <button
                                    onClick={() => {
                                      setShowCreditsModal(user);
                                      setShowActionMenu(null);
                                      fetchUserCredits(user.company_id);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-yellow-400 hover:bg-yellow-500/10 flex items-center gap-2"
                                  >
                                    <Coins className="w-4 h-4" />
                                    Add credits
                                  </button>
                                </>
                              )}

                              <div className="border-t border-gray-700 my-1"></div>

                              <button
                                onClick={() => {
                                  setShowDeleteConfirm(user);
                                  setShowActionMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                              >
                                <Trash2 className="w-4 h-4" />
                                Supprimer
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-black-light rounded-xl border border-brand-green/30 w-full max-w-md">
            <div className="p-6 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Plus className="w-5 h-5 text-brand-green" />
                  Nouvel utilisateur
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-brand-black rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Nom d'utilisateur *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
                  placeholder="jean_dupont"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">E-mail *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
                  placeholder={t('auth.emailPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Mot de passe *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Nom complet</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
                  placeholder="Jean Dupont"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-green"
                >
                  <option value="viewer">Lecteur</option>
                  <option value="analyst">Analyste</option>
                  <option value="manager">Gestionnaire</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={createUser}
                disabled={!formData.username || !formData.email || !formData.password}
                className="px-4 py-2 bg-brand-green text-black rounded-lg hover:bg-brand-green/90 flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-black-light rounded-xl border border-red-500/30 w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-red-500/20 rounded-full">
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Supprimer l'utilisateur ?</h3>
                  <p className="text-gray-400">This action is irreversible</p>
                </div>
              </div>

              <div className="bg-brand-black p-4 rounded-lg mb-4">
                <p className="text-white font-medium">{showDeleteConfirm.username}</p>
                <p className="text-gray-400 text-sm">{showDeleteConfirm.email}</p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => deleteUser(showDeleteConfirm.id)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-brand-black-light rounded-xl border border-brand-green/30 w-full max-w-5xl my-8">
            <div className="p-6 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-brand-green" />
                    Changer le plan
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">
                    {showPlanModal.username} ({showPlanModal.email})
                    {userSubscription && (
                      <span className="ml-2 text-brand-green">
                        • Plan actuel: <span className="font-bold">{
                          {
                            'free': 'Gratuit',
                            'pro': 'Pro',
                            'entreprise': 'Entreprise',
                            'groupe': 'Groupe',
                            'dedicated': 'Dedicated'
                          }[userSubscription.plan] || userSubscription.plan || 'Gratuit'
                        }</span>
                      </span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPlanModal(null);
                    setUserSubscription(null);
                  }}
                  className="p-2 hover:bg-brand-black rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Plan Gratuit */}
                <button
                  onClick={() => setSelectedPlan('free')}
                  className={`p-4 rounded-xl border text-left transition-all hover:-translate-y-1 ${
                    selectedPlan === 'free'
                      ? 'border-gray-400 bg-gray-500/20 ring-2 ring-gray-400'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-500/20 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-gray-400" />
                    </div>
                    {selectedPlan === 'free' && <Check className="w-5 h-5 text-gray-400 ml-auto" />}
                  </div>
                  <h4 className="text-white font-bold">Gratuit</h4>
                  <p className="text-gray-500 text-xs mb-2">Discovery</p>
                  <p className="text-2xl font-bold text-white mb-3">0€</p>
                  <ul className="space-y-1 text-xs text-gray-400">
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-gray-500" /> Mode simulation</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-gray-500" /> 1 utilisateur</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-gray-500" /> 1 cible</li>
                  </ul>
                </button>

                {/* Plan Pro */}
                <button
                  onClick={() => setSelectedPlan('pro')}
                  className={`p-4 rounded-xl border text-left transition-all hover:-translate-y-1 relative ${
                    selectedPlan === 'pro'
                      ? 'border-brand-green bg-brand-green/20 ring-2 ring-brand-green shadow-lg shadow-brand-green/20'
                      : 'border-brand-green/30 hover:border-brand-green/50 bg-gradient-to-b from-brand-green/10 to-transparent'
                  }`}
                >
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                    <span className="bg-brand-green text-black text-xs font-bold px-2 py-0.5 rounded-full">
                      Recommended
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-3 mt-2">
                    <div className="w-10 h-10 rounded-lg bg-brand-green/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-brand-green" />
                    </div>
                    {selectedPlan === 'pro' && <Check className="w-5 h-5 text-brand-green ml-auto" />}
                  </div>
                  <h4 className="text-white font-bold">Pro</h4>
                  <p className="text-brand-green text-xs mb-2">Le plus populaire</p>
                  <p className="text-2xl font-bold text-white mb-3">299€<span className="text-sm text-gray-400">/mois</span></p>
                  <ul className="space-y-1 text-xs text-gray-400">
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-brand-green" /> Unlimited scans</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-brand-green" /> 5 utilisateurs</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-brand-green" /> 10 cibles</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-brand-green" /> Rapports PDF</li>
                  </ul>
                </button>

                {/* Plan Entreprise */}
                <button
                  onClick={() => setSelectedPlan('entreprise')}
                  className={`p-4 rounded-xl border text-left transition-all hover:-translate-y-1 ${
                    selectedPlan === 'entreprise'
                      ? 'border-purple-500 bg-purple-500/20 ring-2 ring-purple-500'
                      : 'border-gray-700 hover:border-purple-500/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-purple-400" />
                    </div>
                    {selectedPlan === 'entreprise' && <Check className="w-5 h-5 text-purple-400 ml-auto" />}
                  </div>
                  <h4 className="text-white font-bold">Entreprise</h4>
                  <p className="text-purple-400 text-xs mb-2">Large teams</p>
                  <p className="text-2xl font-bold text-white mb-3">899€<span className="text-sm text-gray-400">/mois</span></p>
                  <ul className="space-y-1 text-xs text-gray-400">
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-purple-400" /> Tout Pro +</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-purple-400" /> 15 utilisateurs</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-purple-400" /> 30 cibles</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-purple-400" /> SSO / SAML</li>
                  </ul>
                </button>

                {/* Plan Groupe */}
                <button
                  onClick={() => setSelectedPlan('groupe')}
                  className={`p-4 rounded-xl border text-left transition-all hover:-translate-y-1 ${
                    selectedPlan === 'groupe'
                      ? 'border-cyan-500 bg-cyan-500/20 ring-2 ring-cyan-500'
                      : 'border-gray-700 hover:border-cyan-500/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                      <Rocket className="w-5 h-5 text-cyan-400" />
                    </div>
                    {selectedPlan === 'groupe' && <Check className="w-5 h-5 text-cyan-400 ml-auto" />}
                  </div>
                  <h4 className="text-white font-bold">Groupe</h4>
                  <p className="text-cyan-400 text-xs mb-2">Grandes organisations</p>
                  <p className="text-2xl font-bold text-white mb-3">1499€<span className="text-sm text-gray-400">/mois</span></p>
                  <ul className="space-y-1 text-xs text-gray-400">
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-cyan-400" /> Tout Entreprise +</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-cyan-400" /> Unlimited users</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-cyan-400" /> 50 cibles</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-cyan-400" /> Support 24/7</li>
                  </ul>
                </button>

                {/* Dedicated Plan */}
                <button
                  onClick={() => setSelectedPlan('dedicated')}
                  className={`p-4 rounded-xl border text-left transition-all hover:-translate-y-1 ${
                    selectedPlan === 'dedicated'
                      ? 'border-orange-500 bg-orange-500/20 ring-2 ring-orange-500'
                      : 'border-gray-700 hover:border-orange-500/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                      <Settings2 className="w-5 h-5 text-orange-400" />
                    </div>
                    {selectedPlan === 'dedicated' && <Check className="w-5 h-5 text-orange-400 ml-auto" />}
                  </div>
                  <h4 className="text-white font-bold">Dedicated</h4>
                  <p className="text-orange-400 text-xs mb-2">Sur-mesure</p>
                  <p className="text-2xl font-bold text-white mb-1">Sur devis</p>
                  <p className="text-xs text-gray-400 mb-2">Custom pricing</p>
                  <ul className="space-y-1 text-xs text-gray-400">
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-orange-400" /> Dedicated infrastructure</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-orange-400" /> Custom integrations</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-orange-400" /> Pentests manuels</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-orange-400" /> Account manager</li>
                  </ul>
                </button>
              </div>
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPlanModal(null);
                  setUserSubscription(null);
                }}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => updateSubscriptionPlan(showPlanModal.company_id, selectedPlan)}
                className="px-6 py-2 bg-brand-green text-black rounded-lg hover:bg-brand-green/90 flex items-center gap-2 font-semibold"
              >
                <Save className="w-4 h-4" />
                Appliquer le plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credits Modal */}
      {showCreditsModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-black-light rounded-xl border border-yellow-500/30 w-full max-w-md">
            <div className="p-6 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Coins className="w-5 h-5 text-yellow-400" />
                  Add credits
                </h3>
                <button
                  onClick={() => {
                    setShowCreditsModal(null);
                    setUserCredits(null);
                    setCreditsAmount(100);
                    setCreditsReason('');
                  }}
                  className="p-2 hover:bg-brand-black rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-brand-black p-4 rounded-lg">
                <p className="text-white font-medium">{showCreditsModal.username}</p>
                <p className="text-gray-400 text-sm">{showCreditsModal.email}</p>
                {userCredits && (
                  <p className="text-yellow-400 text-sm mt-2">
                    Current credits: <span className="font-bold">{userCredits.total_credits || 0}</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Number of credits to add</label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[50, 100, 250, 500].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCreditsAmount(amount)}
                      className={`p-2 rounded-lg border text-center transition-all ${
                        creditsAmount === amount
                          ? 'border-yellow-500 bg-yellow-500/20 text-yellow-400'
                          : 'border-gray-700 hover:border-gray-600 text-gray-300'
                      }`}
                    >
                      {amount}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  value={creditsAmount}
                  onChange={(e) => setCreditsAmount(parseInt(e.target.value) || 0)}
                  min="1"
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  placeholder="Or enter a custom amount"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Raison (obligatoire)</label>
                <input
                  type="text"
                  value={creditsReason}
                  onChange={(e) => setCreditsReason(e.target.value)}
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  placeholder="e.g. Loyalty bonus, compensation..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreditsModal(null);
                  setUserCredits(null);
                  setCreditsAmount(100);
                  setCreditsReason('');
                }}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => addBonusCredits(showCreditsModal.company_id)}
                disabled={creditsAmount < 1 || !creditsReason}
                className="px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-4 h-4" />
                Add {creditsAmount} credits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-black-light rounded-xl border border-yellow-500/30 w-full max-w-md">
            <div className="p-6 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-yellow-400" />
                  Changer le mot de passe
                </h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(null);
                    setNewPassword('');
                  }}
                  className="p-2 hover:bg-brand-black rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-brand-black p-4 rounded-lg">
                <p className="text-white font-medium">{showPasswordModal.username}</p>
                <p className="text-gray-400 text-sm">{showPasswordModal.email}</p>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-yellow-400 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  The password will be changed immediately
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  placeholder="Minimum 8 characters"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPasswordModal(null);
                  setNewPassword('');
                }}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => resetUserPassword(showPasswordModal.id)}
                disabled={actionLoading || !newPassword || newPassword.length < 8}
                className="px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Key className="w-4 h-4" />
                {actionLoading ? 'Modification...' : 'Modifier le mot de passe'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close action menu */}
      {showActionMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowActionMenu(null)}
        />
      )}
    </div>
  );
};

export default ClientsManagement;
